import {
	ChangeDetectionStrategy,
	Component,
	HostBinding,
	Input,
	Output,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChange,
	ViewEncapsulation,
	EventEmitter,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { ANNUAL_DATA_VALIDATION_ALERT_DIALOG_ID, AnnualDataValidationAlertContainer } from "@obg/mat.ui.common";
import { DialogService } from "@obg/mat.ui.core";
import { CORE_ROUTE, CoreState, CustomerManagementSelector, RequiredActions } from "@obg/ngx.core";
import { coerceBoolean, DateService } from "@obg/ngx.global";
import { RouteAction } from "@obg/ngx.router";
import { Subscription } from "rxjs";
import { map } from "rxjs/operators";

@Component({
	selector: "obg-m-annual-validation-notification",
	templateUrl: "./annual-validation-notification.component.html",
	styleUrls: ["./annual-validation-notification.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class AnnualValidationNotificationComponent implements OnInit, OnChanges, OnDestroy {
	@Input() @coerceBoolean() expired = false;
	@Input() @coerceBoolean() processing = false;
	@Input() @coerceBoolean() annualValidation = false;
	@Input() @coerceBoolean() documentValidation = false;
	@Input() @coerceBoolean() socialBenefits = false;
	@Input() @coerceBoolean() desktop = false;

	@Output() dismissNotification = new EventEmitter();

	activeFromDate: string | undefined;
	data$$: Subscription | undefined;

	title: string | undefined;
	label: string | undefined;
	button: string | undefined;

	@HostBinding("class.obg-m-annual-validation-notification") @coerceBoolean() annualNotification = false;
	@HostBinding("class.obg-m-annual-validation-notification-expired") @coerceBoolean() annualExpired = false;
	@HostBinding("class.obg-m-document-validation-notification") @coerceBoolean() documentNotification = false;
	@HostBinding("class.obg-m-document-validation-notification-expired") @coerceBoolean() documentExpired = false;
	@HostBinding("class.obg-m-document-validation-notification-processing") @coerceBoolean() isProcessing = false;
	@HostBinding("class.obg-m-social-benefits-notification") @coerceBoolean() socialBenefitsNotification = false;

	constructor(
		private dateService: DateService,
		private dialogService: DialogService,
		private store: Store<CoreState>
	) {}

	ngOnInit() {
		if (this.socialBenefits) {
			return;
		}

		this.data$$ = this.store
			.select(CustomerManagementSelector.getRequiredActions)
			.pipe(
				map((requiredActions) => {
					let requiredValidation;
					if (this.annualValidation) {
						requiredValidation = requiredActions?.find(
							(action) => action.actionType === RequiredActions.AccountDataValidation
						);
					}

					if (this.documentValidation) {
						requiredValidation = requiredActions?.find(
							(action) => action.actionType === RequiredActions.DocumentUpload
						);
					}

					const rawDate = new Date(requiredValidation!.activeFrom);
					this.activeFromDate = this.dateService.format(rawDate, "longDate") ?? undefined;
				})
			)
			.subscribe();
	}

	ngOnChanges(changes: {
		expired: SimpleChange;
		annualValidation: SimpleChange;
		documentValidation: SimpleChange;
		socialBenefits: SimpleChange;
		processing: SimpleChange;
	}) {
		this.annualNotification = false;
		this.annualExpired = false;
		this.documentExpired = false;
		this.documentNotification = false;
		this.socialBenefitsNotification = false;
		this.isProcessing = false;

		if (changes.socialBenefits?.currentValue) {
			this.getTranslations();
			this.socialBenefitsNotification = true;
			return;
		}

		if (changes.annualValidation?.currentValue) {
			this.getTranslations();
			if (changes.expired?.currentValue) {
				this.annualExpired = true;
				return;
			}
			this.annualNotification = true;
			return;
		}

		if (changes.processing) {
			const isProcessing = changes.processing.currentValue;
			this.isProcessing = isProcessing;

			if (!this.isProcessing && this.documentValidation) {
				if (this.expired) {
					this.documentExpired = true;
				} else {
					this.documentNotification = true;
				}
			}

			this.getTranslations();
			return;
		}

		if (changes.documentValidation?.currentValue) {
			this.getTranslations();
			if (changes.expired?.currentValue) {
				this.documentExpired = true;
				return;
			}
			this.documentNotification = true;
		}
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}

	onVerify(): void {
		if (this.socialBenefits) {
			return;
		}

		if (this.annualValidation) {
			this.dialogService.openComponent(AnnualDataValidationAlertContainer, {
				id: ANNUAL_DATA_VALIDATION_ALERT_DIALOG_ID,
				disableClose: true,
				closeOnNavigation: false,
			});
		}

		if (this.documentValidation) {
			this.store.dispatch(RouteAction.navigate(CORE_ROUTE.responsibleGaming.documentVerification));
		}
	}

	private getTranslations(): void {
		if (this.socialBenefits) {
			this.label = "account-details.socialbenefits.message";
			return;
		}

		if (this.annualValidation) {
			this.label = this.expired
				? "account-details.annual-data-validation.expired.label"
				: "account-details.annual-data-validation.notification.label";

			this.button = this.expired
				? "account-details.annual-data-validation.expired.cta"
				: "account-details.annual-data-validation.notification.verify";
			return;
		}

		if (this.processing) {
			this.label = "payment.document-is-processing";
			return;
		}

		if (this.documentValidation) {
			this.label = this.expired
				? "account-details.document-validation.expired.label"
				: "account-details.document-validation.notification.label";

			this.button = this.expired
				? "account-details.document-validation.expired.cta"
				: "account-details.document-validation.notification.cta";
			return;
		}
	}
}
